/**
 * `sessionStore` — **qui joue, et où sa partie est calculée.**
 *
 * Deux questions, une seule machine à états, parce qu'elles n'en font qu'une pour le joueur :
 * « bac à sable ou compte ? », puis « connecté ? ». Rien de jeu ici — pas un `GameState`, pas
 * une jauge : ce store décide seulement **quelle porte est ouverte** avant que `runStore` ne
 * charge quoi que ce soit.
 *
 * ⟦A-141 · C17⟧ **L'écran-titre remplace l'ancien écran « bac à sable ou compte ? ».** Le choix
 * du monde n'est plus une question posée en préalable : il vit désormais comme une ligne du
 * formulaire de création (`NouvelleRun.tsx`). Ce que ce store pose en préalable, c'est UNE
 * chose : « as-tu une partie à reprendre ? » — sinon, c'est le menu, jamais un formulaire
 * qu'on découvre sans l'avoir demandé.
 *
 * ```
 *   boot ──┬─ (rien de mémorisé) ───────────────────────────────────────────────► menu
 *          ├─ (bac à sable mémorisé, sans sauvegarde) ────────────────────────────► menu
 *          ├─ (bac à sable mémorisé, une sauvegarde) ───────────────────────────────► jeu
 *          └─ (compte mémorisé) ─► GET /api/auth/get-session ─┬─ pas de session ───► menu
 *                                                              └─ session, run active ─► jeu
 *                                                              └─ session, rien à reprendre ► menu
 * ```
 *
 * Depuis le menu, « Nouvelle partie » et « Charger » sont les deux seules portes vers `jeu` —
 * `Menu.tsx` appelle `chargerPartie()` pour la seconde, qui reprend exactement la logique
 * ci-dessus mais sur une action explicite (donc autorisée à taper le réseau et, en cas d'échec
 * compte, à atterrir sur `auth` plutôt que de revenir bêtement au menu).
 *
 * **Le `401` est un état, pas une exception.** Un cookie expire au bout de 30 jours (§4.1), une
 * base peut être réinitialisée : n'importe quel appel peut donc revenir non authentifié. Plutôt
 * que de le traiter dans quinze appelants, `api/http.ts` prévient ici, une fois, et le joueur
 * revient à l'écran de connexion avec sa partie intacte côté serveur.
 */

import { create } from "zustand";

import { seConnecter, seDeconnecter, sInscrire, sessionCourante, ErreurAuth, type Utilisateur } from "../api/auth";
import { surSessionPerdue } from "../api/http";
import { chargerPreferencesCompte, definirMode, modeMemorise, oublierMode, type ModeJeu } from "../game/client";
import { verifierContenuServeur } from "../game/content";
import { existeSauvegardeLocale } from "../game/localGameClient";

import { useRunStore } from "./runStore";
import { useUIStore } from "./uiStore";

/**
 * Là où en est la porte d'entrée. `jeu` est le seul état où le routeur du jeu s'affiche.
 * `menu` est l'écran-titre ⟦A-141⟧ : le nouveau point d'entrée par défaut, dès qu'il n'y a rien
 * à reprendre automatiquement.
 */
export type StatutSession = "boot" | "menu" | "auth" | "jeu";

/** L'écran d'authentification montre l'un ou l'autre — jamais les deux à la fois. */
export type VueAuth = "connexion" | "inscription";

interface SessionStore {
  statut: StatutSession;
  mode: ModeJeu | null;
  utilisateur: Utilisateur | null;
  vue: VueAuth;
  /** Un appel réseau est en vol : les boutons du formulaire se désactivent. */
  enCours: boolean;
  erreur: string | null;
  initialiser(): Promise<void>;
  choisir(mode: ModeJeu): Promise<void>;
  setVue(vue: VueAuth): void;
  inscrire(input: { name: string; email: string; password: string }): Promise<boolean>;
  connecter(input: { email: string; password: string }): Promise<boolean>;
  deconnecter(): Promise<void>;
  /** Revenir à l'écran-titre sans se déconnecter — le mode choisi est oublié. */
  revenirAuMenu(): void;
  /**
   * ⟦C17⟧ « Menu principal » (Réglages, en partie) : montre l'écran-titre SANS toucher au
   * miroir de la run ni au mode mémorisé — à la différence de `revenirAuMenu()`. C'est ce qui
   * permet au menu d'offrir « Quitter » (revenir ici) ET « Charger » (y retourner d'un clic,
   * sans recharger quoi que ce soit) pour la même partie.
   */
  voirMenu(): void;
  /**
   * ⟦C17⟧ Le bouton « Charger » du menu : reprend la partie mémorisée. Contrairement à
   * `initialiser()`, c'est une action explicite du joueur — un échec de session compte
   * atterrit donc sur l'écran de connexion (avec le message « ta partie t'attend »), pas de
   * retour silencieux au menu.
   */
  chargerPartie(): Promise<void>;
}

/**
 * Vide le miroir de jeu — appelé à chaque changement de client. `pret` retombe à `false` :
 * un vrai chargement doit suivre (`runStore.charger()`), sans quoi `Routeur` resterait bloqué
 * sur « Ouverture du studio… ».
 */
function oublierLaRun(): void {
  useRunStore.setState({
    run: null,
    pret: false,
    deverrouillagesEnAttente: [],
    paliersEnAttente: [],
    chute: null,
    victoire: null,
    recapEnAttente: null,
  });
}

/**
 * ⟦C17⟧ La variante « on sait déjà qu'il n'y a rien à charger » : contrairement à
 * `oublierLaRun()`, `pret` reste `true`. C'est ce qui permet au menu — et à ses Paramètres — de
 * s'afficher sans jamais flasher « Ouverture du studio… » : on n'attend aucun chargement, on a
 * juste constaté qu'aucune partie n'est active.
 */
function entrerAuMenu(): void {
  useRunStore.setState({
    run: null,
    pret: true,
    deverrouillagesEnAttente: [],
    paliersEnAttente: [],
    chute: null,
    victoire: null,
    recapEnAttente: null,
  });
}

export const useSessionStore = create<SessionStore>((set, get) => ({
  statut: "boot",
  mode: null,
  utilisateur: null,
  vue: "connexion",
  enCours: false,
  erreur: null,

  async initialiser() {
    const memorise = modeMemorise();

    if (memorise === null) {
      // Première visite : rien à reprendre, l'écran-titre s'affiche.
      entrerAuMenu();
      set({ statut: "menu", mode: null });
      return;
    }

    if (memorise === "bac-a-sable") {
      definirMode("bac-a-sable");
      // ⟦A-141⟧ Le mode était mémorisé, mais ça ne veut pas dire qu'une partie existe (chaîne
      // abandonnée, ou jamais créée) : sans sauvegarde, direction le menu, jamais le formulaire
      // de création en silence.
      if (!existeSauvegardeLocale()) {
        entrerAuMenu();
        set({ statut: "menu", mode: "bac-a-sable", utilisateur: null });
        return;
      }
      set({ statut: "jeu", mode: "bac-a-sable", utilisateur: null });
      await useRunStore.getState().charger();
      return;
    }

    // Mode compte : la seule façon de savoir si le cookie vaut encore quelque chose est de le
    // demander. `sessionCourante` ne lève jamais — un serveur injoignable rend `null`, et on
    // atterrit sur le menu, d'où le bac à sable reste accessible.
    const utilisateur = await sessionCourante();
    if (!utilisateur) {
      entrerAuMenu();
      set({ statut: "menu", mode: "compte", utilisateur: null });
      return;
    }
    definirMode("compte");
    set({ statut: "jeu", mode: "compte", utilisateur });
    await entrerEnCompte();
    // ⟦A-141⟧ Session valide, mais rien à reprendre (chaîne finie, jamais commencée) : même
    // traitement que le bac à sable, le menu plutôt que le formulaire en silence.
    if (!useRunStore.getState().run) {
      entrerAuMenu();
      set({ statut: "menu" });
    }
  },

  async chargerPartie() {
    const memorise = modeMemorise();
    if (memorise === "bac-a-sable") {
      definirMode("bac-a-sable");
      set({ statut: "jeu", mode: "bac-a-sable", utilisateur: null });
      await useRunStore.getState().charger();
      return;
    }
    if (memorise === "compte") {
      const utilisateur = await sessionCourante();
      if (!utilisateur) {
        // ⟦C17⟧ Action explicite : on ne renvoie pas silencieusement au menu, on dit pourquoi
        // et on ouvre la porte pour se reconnecter — exactement le message de `surSessionPerdue`.
        set({
          statut: "auth",
          mode: "compte",
          utilisateur: null,
          vue: "connexion",
          erreur: "Ta session a expiré. Reconnecte-toi : ta partie t'attend.",
        });
        return;
      }
      definirMode("compte");
      set({ statut: "jeu", mode: "compte", utilisateur });
      await entrerEnCompte();
      if (!useRunStore.getState().run) {
        // Le compte est valide mais n'a aucune chaîne active (finie, ou jamais commencée) :
        // « Charger » n'avait rien à reprendre, on le dit plutôt que d'atterrir sur un
        // formulaire de création sans explication.
        useUIStore.getState().toast({
          icone: "📭",
          ton: "info",
          texte: "Aucune chaîne en cours sur ce compte — direction la création.",
        });
      }
      return;
    }
    // Rien de mémorisé : « Charger » n'aurait pas dû être cliquable (le bouton est grisé dans
    // ce cas, `Menu.tsx`) — filet de sécurité silencieux plutôt qu'une exception.
  },

  async choisir(mode) {
    set({ erreur: null, mode });
    if (mode === "bac-a-sable") {
      definirMode("bac-a-sable");
      oublierLaRun();
      set({ statut: "jeu", utilisateur: null });
      await useRunStore.getState().charger();
      return;
    }
    // Compte : on ne mémorise le mode **qu'une fois connecté**. Le mémoriser avant enfermerait
    // un joueur sans compte dans un écran de connexion à chaque visite.
    const utilisateur = await sessionCourante();
    if (utilisateur) {
      definirMode("compte");
      oublierLaRun();
      set({ statut: "jeu", utilisateur });
      await entrerEnCompte();
      return;
    }
    set({ statut: "auth", vue: "connexion", utilisateur: null });
  },

  setVue(vue) {
    set({ vue, erreur: null });
  },

  async inscrire(input) {
    return entrer(set, get, () => sInscrire(input));
  },

  async connecter(input) {
    return entrer(set, get, () => seConnecter({ email: input.email, password: input.password }));
  },

  async deconnecter() {
    set({ enCours: true });
    try {
      await seDeconnecter();
    } catch {
      // Le cookie est peut-être déjà mort côté serveur : ça n'empêche pas de sortir d'ici.
    } finally {
      oublierMode();
      entrerAuMenu();
      set({ statut: "menu", mode: null, utilisateur: null, enCours: false, erreur: null });
    }
  },

  revenirAuMenu() {
    oublierMode();
    entrerAuMenu();
    set({ statut: "menu", mode: null, erreur: null });
  },

  voirMenu() {
    set({ statut: "menu", erreur: null });
  },
}));

/**
 * ⟦A-8 / M2.40 · A-81⟧ **Ce qui se passe à CHAQUE entrée en mode compte**, et au même endroit
 * pour les trois portes (boot avec session, choix du compte, inscription/connexion) : charger la
 * run, relire les préférences du compte, et vérifier que le contenu du serveur est celui de cette
 * page. Les deux derniers ne bloquent pas la partie — ils se font en parallèle du chargement,
 * et un réseau qui tombe les laisse muets.
 */
async function entrerEnCompte(): Promise<void> {
  void chargerPreferencesCompte();
  void verifierContenuServeur().then((derive) => {
    if (!derive) return;
    useUIStore.getState().toast({
      icone: "🔄",
      ton: "mal",
      texte: `Le serveur joue avec le contenu ${derive.serveur}, cette page avec ${derive.client} : recharge-la pour être à jour.`,
    });
  });
  await useRunStore.getState().charger();
}

/**
 * Le corps commun de l'inscription et de la connexion : les deux finissent exactement pareil —
 * mémoriser le mode compte, vider le miroir, charger la run du serveur.
 */
async function entrer(
  set: (partiel: Partial<SessionStore>) => void,
  get: () => SessionStore,
  appel: () => Promise<Utilisateur>,
): Promise<boolean> {
  if (get().enCours) return false;
  set({ enCours: true, erreur: null });
  try {
    const utilisateur = await appel();
    definirMode("compte");
    oublierLaRun();
    set({ statut: "jeu", mode: "compte", utilisateur, enCours: false });
    await entrerEnCompte();
    return true;
  } catch (e) {
    set({
      enCours: false,
      erreur: e instanceof ErreurAuth ? e.message : "Connexion impossible pour le moment.",
    });
    return false;
  }
}

// Un `401` sur n'importe quelle route `/api/*` ⇒ la session est morte. On ne recharge pas la
// page, on ne perd rien : la partie vit côté serveur, on redemande simplement l'identité.
surSessionPerdue(() => {
  const { statut } = useSessionStore.getState();
  if (statut === "auth" || statut === "menu") return;
  oublierLaRun();
  useSessionStore.setState({
    statut: "auth",
    mode: "compte",
    utilisateur: null,
    vue: "connexion",
    erreur: "Ta session a expiré. Reconnecte-toi : ta partie t'attend.",
  });
});
